/**
 * Methodology & literature for behavioral / dollar ROI framing (linked from Analytics).
 */

import { Link } from 'react-router-dom'
import { ArrowLeft, BookOpen } from 'lucide-react'
import { useAuthStore } from '../store/authStore'

const WARM_CARD = '#FDFCF8'
const WARM_BORDER = '#e0d9cc'

function RefLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-brand-teal font-medium underline underline-offset-2 break-all"
    >
      {children}
    </a>
  )
}

export function BehavioralRoiMethodologyPage() {
  const user = useAuthStore((s) => s.user)
  const canSeeOrgAnalytics = user?.role === 'admin' || user?.role === 'manager'

  return (
    <div className="min-h-screen" style={{ background: '#F5F2EA' }}>
      <header className="border-b border-warm-border bg-warm-card/80 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-6 py-3 flex flex-wrap items-center justify-between gap-3">
          <Link to="/" className="text-sm font-semibold text-ink" style={{ fontFamily: 'Georgia, serif' }}>
            Duva
          </Link>
          <nav className="flex items-center gap-4 text-xs font-medium">
            {user ? (
              <>
                {canSeeOrgAnalytics ? (
                  <Link to="/dashboard/analytics" className="text-brand-teal hover:text-brand-teal-hover">
                    Analytics
                  </Link>
                ) : null}
                <Link to="/dashboard" className="text-ink-muted hover:text-brand-teal">
                  Dashboard
                </Link>
              </>
            ) : (
              <Link to="/login" className="text-ink-muted hover:text-brand-teal">
                Sign in
              </Link>
            )}
          </nav>
        </div>
      </header>

      <div className="p-8 max-w-3xl mx-auto pb-16">
      <Link
        to={user ? (canSeeOrgAnalytics ? '/dashboard/analytics' : '/dashboard') : '/'}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-teal hover:text-brand-teal-hover mb-6"
      >
        <ArrowLeft size={14} />
        {user ? (canSeeOrgAnalytics ? 'Back to Analytics' : 'Back to dashboard') : 'Back to home'}
      </Link>

      <div
        className="rounded-2xl border p-6 shadow-card mb-8"
        style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
      >
        <div className="flex items-center gap-2 mb-3">
          <BookOpen size={22} className="text-brand-teal" />
          <h1
            className="text-2xl font-bold text-ink"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Behavioral ROI — methodology & research
          </h1>
        </div>
        <p className="text-sm text-ink-muted leading-relaxed">
          Duva may show <strong>illustrative dollar estimates</strong> on Analytics by multiplying{' '}
          <strong>completed nudge actions</strong> by a fixed <strong>per-action assumption</strong>. That figure is a{' '}
          <strong>modeling shortcut</strong>, not a measured financial outcome for your organization. This page explains
          how that fits with the academic literature and why peer-reviewed work rarely endorses a single universal
          “dollars per micro-nudge.”
        </p>
      </div>

      <article className="space-y-8 text-ink">
        <section className="space-y-3">
          <h2 className="text-lg font-semibold" style={{ color: '#1a3a40' }}>
            Illustrative benchmarks vs. scientific framing
          </h2>
          <p className="text-sm text-ink-muted leading-relaxed">
            Specific figures such as <strong>$2 or $5 per micro-nudge</strong> often appear as{' '}
            <strong>illustrative benchmarks</strong> in corporate HR and behavioral-design tooling. Formal scientific
            literature more commonly frames the “value” of nudges in terms of{' '}
            <strong>effect sizes</strong>, <strong>cost-effectiveness</strong>, or <strong>ROI relative to alternatives</strong>{' '}
            (e.g. cash incentives)—not as a single industry-wide dollar bounty per click or per reminder.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            The research landscape for workplace nudges is growing but still described as{' '}
            <strong>early</strong> when it comes to precise, generalizable{' '}
            <strong>dollar-value-per-action</strong> metrics{' '}
            <RefLink href="https://doi.org/10.1027/1866-5888/a000362">(Pinel et al., 2024)</RefLink>; broader reviews
            across domains also emphasize heterogeneity and context{' '}
            <RefLink href="https://doi.org/10.1073/pnas.2107346118">(Mertens et al., 2022)</RefLink>.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-lg font-semibold" style={{ color: '#1a3a40' }}>
            Cited work relevant to economic framing
          </h2>

          <div
            className="rounded-xl border p-4 space-y-2"
            style={{ background: '#f9f7f2', borderColor: WARM_BORDER }}
          >
            <h3 className="text-sm font-semibold text-ink">1. Large-scale organizational field experiments</h3>
            <p className="text-sm text-ink-muted leading-relaxed">
              <strong>Kristal et al. (2019)</strong> report five naturalistic field experiments involving{' '}
              <strong>68,915 employees</strong> of a single organization, testing established behavioral interventions
              around <strong>commuting choices</strong>. The study is widely cited for{' '}
              <strong>transparent reporting of null or near-null results</strong> in the field—highlighting that even
              large, well-powered workplace nudge programs may not move outcomes as expected, and that{' '}
              <strong>rigorous measurement</strong> matters more than assumed per-action dollar values.{' '}
              <RefLink href="https://doi.org/10.1038/s41562-019-0795-z">
                Kristal et al. (2019), <em>Nature Human Behaviour</em>
              </RefLink>
              .
            </p>
          </div>

          <div
            className="rounded-xl border p-4 space-y-2"
            style={{ background: '#f9f7f2', borderColor: WARM_BORDER }}
          >
            <h3 className="text-sm font-semibold text-ink">2. Nudges vs. financial incentives (reviews)</h3>
            <p className="text-sm text-ink-muted leading-relaxed">
              Reviews often contrast <strong>low-cost nudges</strong> with <strong>tangible incentives</strong>, noting
              when reminders and choice architecture achieve change at lower marginal cost—while also stressing limits,
              ethics, and context{' '}
              <RefLink href="https://doi.org/10.1027/1866-5888/a000362">(Pinel et al., 2024)</RefLink>. A scoping review
              in neglected-tropical-disease prevention discusses nudge strategies and ethical trade-offs across
              settings{' '}
              <RefLink href="https://doi.org/10.1371/journal.pntd.0009239">(Vande Velde et al., 2021)</RefLink>—useful
              for <strong>how reviews structure “low-cost vs. incentive” comparisons</strong>, even though the domain
              differs from corporate SaaS.
            </p>
          </div>

          <div
            className="rounded-xl border p-4 space-y-2"
            style={{ background: '#f9f7f2', borderColor: WARM_BORDER }}
          >
            <h3 className="text-sm font-semibold text-ink">3. Meta-analytic evidence (effect sizes & domains)</h3>
            <p className="text-sm text-ink-muted leading-relaxed">
              <strong>Mertens et al. (2022)</strong> meta-analyze choice-architecture interventions across behavioral
              domains; the emphasis is typically on <strong>average effects and heterogeneity</strong>, not a universal
              “price tag” per reminder. Energy and pro-environmental studies sometimes translate percentage savings
              into dollars at the <strong>facility level</strong>—which still depends on local prices and baseline use, not
              a fixed global constant per nudge.{' '}
              <RefLink href="https://doi.org/10.1073/pnas.2107346118">
                Mertens et al. (2022), <em>PNAS</em>
              </RefLink>
              .
            </p>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold" style={{ color: '#1a3a40' }}>
            Research gaps — and how Duva presents numbers
          </h2>
          <p className="text-sm text-ink-muted leading-relaxed">
            Most peer-reviewed studies emphasize <strong>effect sizes</strong> and <strong>variance across contexts</strong>{' '}
            rather than a single “bounty” like $5 per nudge{' '}
            <RefLink href="https://doi.org/10.1073/pnas.2107346118">(Mertens et al., 2022)</RefLink>. Commercial
            platforms sometimes publish <strong>illustrative</strong> per-action or per-hour valuations (e.g. meeting time
            or retention) to explain software value—those should be read as{' '}
            <strong>assumptions for planning</strong>, not peer-reviewed causal dollar ROI for every customer.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            In Duva, treat dashboard dollar estimates as <strong>scenario math</strong> on top of{' '}
            <strong>observed completion counts</strong>. They are <strong>not</strong> audited financial advice or a
            guarantee of savings.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold" style={{ color: '#1a3a40' }}>
            References
          </h2>
          <ol className="list-decimal pl-5 space-y-3 text-sm text-ink-muted">
            <li>
              Mertens, S., Herberz, M., Hahnel, U. J. J., &amp; Brosch, T. (2022). The effectiveness of nudging: A
              meta-analysis of choice architecture interventions across behavioral domains.{' '}
              <em>Proceedings of the National Academy of Sciences</em>, <em>119</em>(1), e2107346118.{' '}
              <RefLink href="https://doi.org/10.1073/pnas.2107346118">https://doi.org/10.1073/pnas.2107346118</RefLink>
            </li>
            <li>
              Pinel, H., Sesini, G., &amp; Lozza, E. (2024). Nudges in Workplace Environments – A Systematic Review and
              Research Agenda. <em>Journal of Personnel Psychology</em>.{' '}
              <RefLink href="https://doi.org/10.1027/1866-5888/a000362">
                https://doi.org/10.1027/1866-5888/a000362
              </RefLink>
            </li>
            <li>
              Vande Velde, F., Overgaard, H. J., &amp; Bastien, S. (2021). Nudge strategies for behavior-based
              prevention and control of neglected tropical diseases: A scoping review and ethical assessment.{' '}
              <em>PLOS Neglected Tropical Diseases</em>, <em>15</em>(11), e0009239.{' '}
              <RefLink href="https://doi.org/10.1371/journal.pntd.0009239">
                https://doi.org/10.1371/journal.pntd.0009239
              </RefLink>
            </li>
            <li>
              Kristal, A. D., Whillans, A. V., Bazerman, M. H., Gino, F., Shu, L. L., Mazar, N., &amp; Ariely, D. (2019).
              What we can learn from five naturalistic field experiments that failed to shift commuter behaviour.{' '}
              <em>Nature Human Behaviour</em>.{' '}
              <RefLink href="https://doi.org/10.1038/s41562-019-0795-z">
                https://doi.org/10.1038/s41562-019-0795-z
              </RefLink>{' '}
              (68,915 employees across experiments; commuting behaviors).
            </li>
          </ol>
        </section>

        <p className="text-xs text-ink-faint pt-4 border-t border-warm-border">
          Questions about how your organization should value habit or learning interventions belong to your HR, finance,
          and legal partners. This page is informational and may be updated as the product evolves.
        </p>
      </article>
      </div>
    </div>
  )
}
